// Admin API client: httpOnly session cookie (dd_admin) + CSRF header on every write (Guide §17.1). No tokens in JS-readable storage.
const BASE = '/admin-api/v1';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public messageEn: string, public details?: unknown) { super(code); }
  get text(): string {
    const d = this.details as { reason?: string; missing?: string; perm?: string; until?: string } | undefined;
    const extra = d?.reason ?? d?.missing ?? (d?.perm ? `needs ${d.perm}` : '');
    return extra ? `${this.messageEn} (${extra})` : this.messageEn;
  }
}

let csrf: string | null = null;
export const setCsrf = (t: string | null): void => { csrf = t; };
type Listener = () => void;
const unauth = new Set<Listener>();
export const onUnauthenticated = (l: Listener): (() => void) => { unauth.add(l); return () => unauth.delete(l); };

export interface Opts { json?: unknown; query?: Record<string, string | number | boolean | undefined | null>; body?: string; contentType?: string }

export async function request<T = unknown>(method: string, path: string, o: Opts = {}): Promise<T> {
  let url = `${BASE}${path}`;
  if (o.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(o.query)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    if ([...qs].length) url += `?${qs.toString()}`;
  }
  const headers: Record<string, string> = {};
  if (o.json !== undefined) headers['content-type'] = 'application/json';
  if (o.contentType) headers['content-type'] = o.contentType;
  if (method !== 'GET' && csrf) headers['x-csrf-token'] = csrf;
  let res: Response;
  try { res = await fetch(url, { method, credentials: 'include', headers, body: o.json !== undefined ? JSON.stringify(o.json) : o.body }); }
  catch { throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.'); }
  if (!res.ok) {
    let b: { error?: { code?: string; messageEn?: string; details?: unknown } } = {};
    try { b = await res.json(); } catch { /* not json */ }
    const code = b.error?.code ?? 'internal';
    if (res.status === 401 || code === 'token_expired' || code === 'unauthenticated') { if (!path.startsWith('/auth/login') && !path.startsWith('/auth/totp')) unauth.forEach((l) => l()); }
    throw new ApiError(res.status, code, b.error?.messageEn ?? `Request failed (${res.status})`, b.error?.details);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export const api = {
  get: <T = unknown>(p: string, query?: Opts['query']) => request<T>('GET', p, { query }),
  post: <T = unknown>(p: string, json?: unknown) => request<T>('POST', p, { json: json ?? {} }),
  put: <T = unknown>(p: string, json?: unknown) => request<T>('PUT', p, { json }),
  patch: <T = unknown>(p: string, json?: unknown) => request<T>('PATCH', p, { json }),
  del: <T = unknown>(p: string, json?: unknown) => request<T>('DELETE', p, { json }),
  text: <T = unknown>(p: string, body: string) => request<T>('POST', p, { body, contentType: 'text/csv' }),
};
export const errText = (e: unknown): string => (e instanceof ApiError ? e.text : 'Something went wrong.');
